import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight, Phone, Globe2, ShieldCheck, Bitcoin } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { PHONE, scrollToId } from "../../lib/api";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroImage = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} className="relative mt-12 [perspective:1600px] lg:mt-16" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(18% 10% 18% 10% round 32px)", opacity: 0.4 }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 32px)", opacity: 1 }}
        transition={{ duration: 1.6, delay: 0.55, ease: EASE }}
        className="relative h-[44vh] min-h-[300px] overflow-hidden rounded-[32px] bg-ink shadow-[0_40px_120px_-40px_rgba(13,15,18,0.45)] sm:h-[56vh] lg:h-[62vh]"
        data-testid="hero-image-frame"
      >
        <motion.img
          src="/img/hero.jpg"
          alt="Showroom CARS 99"
          style={{ y }}
          className="absolute inset-0 h-[108%] w-full -translate-y-[4%] object-cover"
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,transparent_30%,rgba(13,15,18,0.55)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/60 to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex flex-wrap items-end justify-between gap-4 sm:bottom-8 sm:left-8 sm:right-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/85">N° 99 — Via Cividina, Udine</p>
          <div className="glass-chip hidden sm:flex">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" /> {t.hero.stats[0]} · {t.hero.stats[1]}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t } = useI18n();
  const icons = [Globe2, ShieldCheck, Bitcoin];
  return (
    <section className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <div className="hero-glow" />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.1 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-600 sm:text-xs" data-testid="hero-eyebrow">
          {t.hero.eyebrow}
        </motion.p>
        <div className="mt-6 grid gap-8 lg:grid-cols-12">
          <h1 className="font-display text-[7.2vw] font-extrabold uppercase leading-[0.95] tracking-[-0.02em] text-ink sm:text-5xl lg:col-span-12 lg:text-[4.2rem] xl:text-[4.8rem]" data-testid="hero-title">
            {t.hero.lines.map((line, i) => (
              <MaskLine key={`${line}-${i}`} i={i}>
                {i === t.hero.accent ? <span className="font-serif font-normal normal-case italic tracking-normal text-champagne-dark">{line}</span> : line}
              </MaskLine>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.7, ease: EASE }} className="lg:col-span-5 lg:col-start-8 lg:-mt-4">
            <p className="text-base leading-relaxed text-slate-700">{t.hero.sub}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => scrollToId("inventory")} className="btn-ink group" data-testid="hero-cta-inventory">
                {t.hero.cta1} <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
              </button>
              <a href={`tel:${PHONE}`} className="btn-ghost" data-testid="hero-cta-call"><Phone className="h-4 w-4" /> {t.hero.cta2}</a>
            </div>
          </motion.div>
        </div>
        <HeroImage />
        <div className="grid grid-cols-1 gap-px overflow-hidden border-b border-ink/10 sm:grid-cols-3">
          {t.hero.stats.map((s, i) => {
            const Icon = icons[i];
            return (
              <motion.div key={s} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.2 + i * 0.1, ease: EASE }}
                className="flex items-center gap-4 py-6 sm:py-8" data-testid={`hero-stat-${i}`}>
                <span className="grid h-11 w-11 place-items-center rounded-full border border-ink/10 bg-white"><Icon className="h-5 w-5 text-champagne-dark" /></span>
                <span className="font-display text-base font-bold uppercase tracking-tight text-ink sm:text-lg">{s}</span>
              </motion.div>
            );
          })}
        </div>
        <button onClick={() => scrollToId("inventory")} className="mx-auto mt-6 hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 lg:flex" data-testid="hero-scroll">
          {t.hero.scroll} <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
        </button>
      </div>
    </section>
  );
};
